import React, {useState} from 'react';
import Modal from '../../Components/Modal';
import Form, { Actions, Assets, Conditional, Radio, RegexValidator, RequiredValidator, Select, Switch, TextField, ULID } from '../../Components/Form';
import { categories } from '../../Data/Documents';

const AddDocument = ({addingDocument, setAddingDocument, addDocument, onDocumentAdded}) => {
    return <Modal title="Add Document" isOpen={addingDocument} setIsOpen={setAddingDocument} dismissible>
        {addingDocument && <Inner addDocument={addDocument} onDocumentAdded={onDocumentAdded} />}
    </Modal>
}

const Inner = ({addDocument, onDocumentAdded}) => {
    const [suggestedKey, setSuggestedKey] = useState('');
    const [data, setData] = useState({});

    const handleSubmit = (data) => {
        if (!data.key) {
            data.key = suggestedKey;
        }
        return addDocument(data);
    }

    const handleTemplateChange = (newTemplateClass) => {
        if (newTemplateClass === 'TermsOfService') {
            setData({
                source: 'Template',
                templateClass: newTemplateClass,
                category: 'Agreements',
                title: 'Terms of Service',
                key: 'termsOfService'
            })
        } else if (newTemplateClass === 'PrivacyPolicy') {
            setData({
                source: 'Template',
                templateClass: newTemplateClass,
                category: 'Agreements',
                title: 'Privacy Policy',
                key: 'privacyPolicy'
            })
        } else {
            setData({
                source: 'Template',
                templateClass: newTemplateClass,
            })
        }
    }

    return <Form data={data}
        key={data.templateClass}
        onSubmit={handleSubmit}
        afterSubmit={onDocumentAdded}
        closeModalOnSubmit
        restoreDataOnConditionalChange
    >
        <ULID name="id" />

        <Radio name="source"
            label="Source"
            layout="SmallCards"
            options={[
                {label: 'Upload', value: 'Upload'},
                {label: 'Template', value: 'Template'},
                {label: 'Custom', value: 'Custom'},
            ]}
            help={(v) => {
                const help = {
                    Upload: 'Upload a document you already have.',
                    Template: 'Create a new document using a template.',
                    Custom: 'Create a new document from scratch.',
                }
                return help[v] ?? null;
            }}
            validators={[RequiredValidator]}
        />

        <Conditional condition={d => d.source === 'Template'}>
            <Select name="templateClass"
                label="Template"
                layout="List"
                unselected="Select a template"
                options={[
                    {value: 'TermsOfService', label: 'Terms Of Service'},
                    {value: 'PrivacyPolicy', label: 'Privacy Policy'},
                ]}
                onChange={handleTemplateChange}
                validators={[RequiredValidator]}
            />
        </Conditional>

        <Conditional condition={d => d.source && (d.source !== 'Template' || d.templateClass)}>

            <Select name="category"
                label="Category"
                layout="List"
                unselected="Select a category"
                options={Object.keys(categories).map(c => ({label: c, value: c}))}
                help={v => categories[v] ?? null}
                validators={[RequiredValidator]}
            />

            <TextField name="title"
                label="Title"
                validators={[RequiredValidator]}
                onChange={(v) => {
                    const inCamelCase = v.replace(/[^a-zA-Z0-9]+/g, ' ').replace(/(?:^\w|[A-Z]|\b\w)/g, (word, index) =>
                        index === 0 ? word.toLowerCase() : word.toUpperCase()
                    ).replace(/\s+/g, '');
                    setSuggestedKey(inCamelCase);
                }}
            />

            <TextField name="key"
                label="Key"
                validators={[RegexValidator('^[a-zA-Z0-9]{3,200}$')]}
                placeholder={suggestedKey}
                help={`The key us used to reference this document using template tags.
                    It can only contain letters and numbers, and must be unique.
                    Leave this field blank to use the suggested key based on the title.`}
            />
        </Conditional>

        <Conditional condition={d => d.source === 'Upload'}>
            <Assets name="assetId"
                label="File"
                tags={[{name: 'Documents', value: 'Document'}]}
                validators={[RequiredValidator]}
                accept={['*/*']}
                showPrevious
                single
            />
        </Conditional>

        <Conditional condition={d => d.source === 'Template' && d.templateClass}>
            <Switch name="disclaimer"
                label="Disclaimer"
                innerLabel="I understand the generated document is a guide only and may not be legally sound."
                validators={[RequiredValidator]}
            />
        </Conditional>

        <Actions submit={{label: 'Add Document'}} cancel />
    </Form>
}

export default AddDocument;
